import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, AlertTriangle, CalendarClock, CheckCircle, ChevronLeft, ChevronRight,
  Info, Lock, RefreshCw, X,
} from 'lucide-react';
import * as plannerApi from '../../api/planner';
import PlannedRoute from './PlannedRoute';
import {
  CONFIRMED_LABEL, CONFIRMED_MARK,
  availabilityLabel, availabilityMark, formatDateRange, formatKickOff,
  validityDetail, validityLabel, weekStatusLabel,
} from './plannerLabels';
import './planner.css';

/** The one sentence this whole screen is qualified by. Stated once, always visible. */
const DISCLAIMER =
  'Planned picks are for planning only. Fixtures can change and you will still need to '
  + 'confirm your pick for each gameweek.';

/**
 * The Fixture Planner - look ahead, sketch a route, change your mind.
 *
 * <p><strong>Nothing here picks anything.</strong> Every write goes to the planner
 * endpoints, which touch one table the game never reads. Even for the active gameweek,
 * where a plan and a pick would be about the same fixture, this screen's job ends at
 * "you have this planned" and the player goes to Current Round to confirm it.
 *
 * <p>The whole screen comes from one request. That is not only the performance
 * requirement - it is what stops the screen contradicting itself, since availability, the
 * plan and the plan's validity are three views of the same join and must be computed
 * together or not at all. Every write returns the recomputed planner for the same reason,
 * so there is no window in which one week's card has been updated and the others have not.
 *
 * <p>Mobile-first: one gameweek fills the screen and the others are a swipe away, because
 * five weeks of fixtures side by side is a desktop table and this is read on a phone.
 */
export default function FixturePlanner({ gameId }) {
  const [planner, setPlanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [chosenWeek, setChosenWeek] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setPlanner(await plannerApi.getPlanner(gameId));
    } catch (err) {
      setLoadError(err.name === 'ApiError' && err.status === 403
        ? 'You are not in this game.'
        : 'Could not load the Fixture Planner.');
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const weeks = useMemo(() => planner?.weeks ?? [], [planner]);

  const routeByWeek = useMemo(() => {
    const byWeek = {};
    for (const planned of planner?.route ?? []) byWeek[planned.gameWeek] = planned;
    return byWeek;
  }, [planner]);

  // Open on the first week that still needs a plan, so the screen lands on the decision
  // rather than on a week already settled. Derived rather than held in an effect: the
  // week on screen is a function of the plan, and an effect that copies it into state
  // renders one frame showing neither - which is a visible flicker every time a write
  // comes back, on the screen where writes are the whole interaction.
  const defaultWeek = useMemo(() => {
    if (weeks.length === 0) return null;
    const unplanned = weeks.find((w) => {
      const planned = routeByWeek[w.gameWeek];
      return !planned || planned.validity !== 'VALID';
    });
    return (unplanned ?? weeks[0]).gameWeek;
  }, [weeks, routeByWeek]);

  // The player's own choice wins, until the week they chose stops existing - a fixture
  // sheet rebuild can take a gameweek away while this tab is open, and falling back beats
  // showing nothing.
  const activeWeek = chosenWeek != null && weeks.some((w) => w.gameWeek === chosenWeek)
    ? chosenWeek
    : defaultWeek;

  const week = weeks.find((w) => w.gameWeek === activeWeek) ?? null;
  const weekIndex = week ? weeks.findIndex((w) => w.gameWeek === week.gameWeek) : -1;
  const plannedHere = week ? routeByWeek[week.gameWeek] : null;

  /**
   * Writes replace the whole planner from the response.
   *
   * No optimistic update. Choosing a team in week 8 can invalidate a plan in week 9 and
   * change what is offered in weeks 6 and 7, and guessing at that in the browser is
   * re-implementing the rule the endpoint just applied - which is how the two come to
   * disagree.
   */
  const write = async (action) => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      setPlanner(await action());
    } catch (err) {
      setError(err.name === 'ApiError' ? err.message : 'Could not save your plan.');
    } finally {
      setSaving(false);
    }
  };

  const handlePlan = (team) => {
    if (!week || !team?.selectable) return;
    // Tapping the team already planned here clears it, so the same target both sets and
    // unsets. A separate "clear" control next to every team would double the tap targets
    // on the densest part of the screen.
    if (team.availability === 'PLANNED_THIS_WEEK') {
      return write(() => plannerApi.clearPlannedPick(gameId, week.gameWeek));
    }
    return write(() => plannerApi.planPick(gameId, week.gameWeek, team.id));
  };

  const handleClear = (gameWeek) => write(() => plannerApi.clearPlannedPick(gameId, gameWeek));

  if (loading) {
    return (
      <div className="pl-loading">
        <RefreshCw size={28} className="pl-spin" aria-hidden="true" />
        <p>Loading the Fixture Planner…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="pl-empty">
        <AlertCircle size={40} aria-hidden="true" />
        <h3>The Fixture Planner is unavailable</h3>
        <p>{loadError}</p>
        <button type="button" className="pl-btn" onClick={load}>Try again</button>
      </div>
    );
  }

  return (
    <div className="pl-root">
      {/* Above everything, and not dismissible. The player is about to spend ten minutes
          building something the site is explicitly not promising to honour. */}
      <p className="pl-disclaimer">
        <Info size={16} className="pl-disclaimer-icon" aria-hidden="true" />
        <span>{DISCLAIMER}</span>
      </p>

      {!planner?.active && (
        <p className="pl-inactive" role="status">
          <Lock size={16} aria-hidden="true" />
          <span>
            {planner?.inactiveReason === 'GAME_OVER'
              ? 'This game has finished. Fixtures below are for reference only.'
              : 'You have been eliminated from this game, so you can no longer plan picks. '
                + 'The fixtures below are still here to look at.'}
          </span>
        </p>
      )}

      <p className="pl-remaining">
        <strong>{planner?.teamsRemaining ?? 0}</strong> of {planner?.tournamentTeamCount ?? 0}
        {' '}teams still available to you
      </p>

      {weeks.length === 0 ? (
        <div className="pl-empty">
          <CalendarClock size={40} aria-hidden="true" />
          <h3>No upcoming fixtures yet</h3>
          <p>
            There are no future gameweeks with fixtures for this game. Check back once the
            next fixture sheet is up.
          </p>
        </div>
      ) : (
        <>
          <PlannedRoute
            weeks={weeks}
            routeByWeek={routeByWeek}
            activeWeek={activeWeek}
            onSelectWeek={setChosenWeek}
          />

          {/* The pager. Swiping the card works too, but a phone gesture with no visible
              control is a feature only the person who built it knows about. */}
          <div className="pl-pager">
            <button
              type="button"
              className="pl-pager-btn"
              disabled={weekIndex <= 0}
              aria-label="Previous gameweek"
              onClick={() => setChosenWeek(weeks[weekIndex - 1].gameWeek)}
            >
              <ChevronLeft size={20} aria-hidden="true" />
            </button>
            <div className="pl-pager-label">
              <span className="pl-pager-week">Gameweek {week?.gameWeek}</span>
              <span className="pl-pager-dates">{formatDateRange(week?.firstKickOff, week?.lastKickOff)}</span>
            </div>
            <button
              type="button"
              className="pl-pager-btn"
              disabled={weekIndex < 0 || weekIndex >= weeks.length - 1}
              aria-label="Next gameweek"
              onClick={() => setChosenWeek(weeks[weekIndex + 1].gameWeek)}
            >
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </div>

          {error && (
            <p className="pl-error" role="alert">
              <AlertTriangle size={16} aria-hidden="true" /> {error}
            </p>
          )}

          {week && (
            <WeekCard
              week={week}
              planned={plannedHere}
              saving={saving}
              onPlan={handlePlan}
              onClear={handleClear}
            />
          )}
        </>
      )}

      {/* The game may have moved on while this tab was open - a pick confirmed in another
          window, a fixture resulted by an admin. Offered rather than polled: a planner
          that reloads under the player's thumb is worse than a slightly stale one. */}
      <div className="pl-refresh-row">
        <button type="button" className="pl-btn is-quiet" onClick={load}>
          <RefreshCw size={15} aria-hidden="true" /> Refresh fixtures
        </button>
      </div>
    </div>
  );
}

/** One gameweek: its status, what is planned for it, and its fixtures. */
function WeekCard({ week, planned, saving, onPlan, onClear }) {
  const problem = planned ? validityLabel(planned.validity) : null;
  const statusText = weekStatusLabel(week);

  return (
    <section className="pl-week" aria-label={`Gameweek ${week.gameWeek} fixtures`}>
      <header className="pl-week-head">
        <div className="pl-week-tags">
          {week.current && <span className="pl-tag is-current">Current gameweek</span>}
          {week.tournamentMatchWeek != null && (
            <span className="pl-tag">Match week {week.tournamentMatchWeek}</span>
          )}
          {statusText && (
            <span className={`pl-tag${week.planningAllowed ? '' : ' is-locked'}`}>{statusText}</span>
          )}
        </div>

        {week.officialPick && (
          <p className="pl-official">
            <CheckCircle size={15} aria-hidden="true" />
            <span>
              Your confirmed pick for this gameweek is <strong>{week.officialPick.teamName}</strong>
              {week.officialPick.autoPick ? ' (auto-picked)' : ''}.
            </span>
          </p>
        )}

        {planned && (
          <div className={`pl-planned${problem ? ' is-invalid' : ''}`}>
            <div className="pl-planned-main">
              <span className="pl-planned-label">{problem ? 'Planned (needs replanning)' : 'Planned'}</span>
              <span className="pl-planned-team">{planned.teamName}</span>
              {problem && <span className="pl-planned-problem">{problem}</span>}
            </div>
            {problem && <p className="pl-planned-detail">{validityDetail(planned.validity)}</p>}
            <button
              type="button"
              className="pl-btn is-quiet pl-planned-clear"
              disabled={saving}
              aria-label={`Clear planned pick for gameweek ${week.gameWeek}`}
              onClick={() => onClear(week.gameWeek)}
            >
              <X size={14} aria-hidden="true" /> Clear
            </button>
          </div>
        )}

        {week.current && planned && !problem && (
          // The bridge to the real thing, and deliberately only a signpost. Wiring a
          // "confirm this" button here would make the planner able to submit a pick,
          // which is the one thing it must never be able to do.
          <p className="pl-nudge">
            To make this your pick, confirm it on the <strong>Current Round</strong> tab.
          </p>
        )}

        {!week.planningAllowed && (
          <p className="pl-week-locked">
            <Lock size={14} aria-hidden="true" /> This gameweek cannot be planned.
          </p>
        )}
      </header>

      {week.fixtures.length === 0 ? (
        <p className="pl-week-empty">No fixtures listed for this gameweek.</p>
      ) : (
        /* role="list" is redundant markup that is not redundant in practice: VoiceOver in
           Safari drops list semantics from a list styled with list-style: none, and how
           many fixtures a week has is what this element is for. Same reasoning as
           PlannedRoute. */
        // eslint-disable-next-line jsx-a11y/no-redundant-roles -- deliberate, see above
        <ul className="pl-fixtures" role="list">
          {week.fixtures.map((fixture) => (
            <li key={fixture.gameFixtureEntryId ?? fixture.fixtureId} className="pl-fixture">
              <div className="pl-fixture-time">
                <span>{formatKickOff(fixture.kickOff) || 'Date to be confirmed'}</span>
                {fixture.status === 'VOIDED' && <span className="pl-fixture-void">Called off</span>}
              </div>
              <div className="pl-fixture-teams">
                <TeamButton
                  team={fixture.homeTeam}
                  confirmedTeamId={week.officialPick?.teamId}
                  saving={saving}
                  onPlan={onPlan}
                />
                <span className="pl-fixture-vs" aria-hidden="true">v</span>
                <TeamButton
                  team={fixture.awayTeam}
                  confirmedTeamId={week.officialPick?.teamId}
                  saving={saving}
                  onPlan={onPlan}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One side of one fixture.
 *
 * <p>State is carried by the visible label first and the tint second, which is the
 * accessibility rule this screen most needed: "already used" and "planned for GW9" are
 * both unavailable and are not the same thing, and no shade of grey says which.
 * `aria-pressed` carries the same fact to a screen reader without relying on the label.
 *
 * <p>A confirmed pick outranks whatever availability it arrives with. The server calls it
 * `USED`, which is true and useless here: on the week you actually picked it, the team is
 * the one thing on the card the player is looking for, and "Already used" puts it in with
 * the dozen sides they burned in August.
 */
function TeamButton({ team, confirmedTeamId, saving, onPlan }) {
  if (!team) return <span className="pl-team is-missing">TBC</span>;

  const confirmed = confirmedTeamId != null && String(team.id) === String(confirmedTeamId);
  const label = confirmed ? CONFIRMED_LABEL : availabilityLabel(team);
  const mark = confirmed ? CONFIRMED_MARK : availabilityMark(team);
  const planned = team.availability === 'PLANNED_THIS_WEEK';

  return (
    <button
      type="button"
      className={`pl-team is-${team.availability.toLowerCase().replace(/_/g, '-')}`
        + (confirmed ? ' is-confirmed' : '')}
      disabled={!team.selectable || saving}
      aria-pressed={planned}
      aria-label={confirmed
        ? `${team.name} — your confirmed pick for this gameweek`
        : (planned
          ? `${team.name} — planned for this gameweek. Tap to clear.`
          : `${team.name} — ${label}`)}
      onClick={() => onPlan(team)}
    >
      <span className="pl-team-name">{team.name}</span>
      <span className="pl-team-state">
        {mark && <span className="pl-team-mark" aria-hidden="true">{mark}</span>}
        {label}
      </span>
    </button>
  );
}
